import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { CustomNumberField } from '../../../../../components/FormFields/CustomField/CustomNumberField';
import { TransactionFeeScheduleItem } from '../../../../../slices/marketplace/pricing/models/transaction-fee-schedule-item.model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import Helpers from '../../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormikErrors } from 'formik';
import './TransactionFeeScheduleTable.scss';
import { MarketplacePercentField } from '../MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';

interface TransactionFeeScheduleTableProps {
    pricing: MarketplacePricing;
    setValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
        rowAddedIndex?: number,
        rowDeletedIndex?: number
    ) => Promise<void | FormikErrors<MarketplacePricing>>;
}

const TransactionFeeScheduleTable = (
    props: TransactionFeeScheduleTableProps
) => {
    const deleteRow = (index: number) => {
        let newDiscountState =
            props.pricing.discountBrokeragePricing.transactionFeeSchedule.map(
                (item, idx) => {
                    if (idx > index) {
                        return { ...item, index: item.index - 1 };
                    }
                    return item;
                }
            );
        newDiscountState.splice(index, 1);

        let newFsState =
            props.pricing.fullServiceBrokeragePricing.transactionFeeSchedule.map(
                (item, idx) => {
                    if (idx > index) {
                        return { ...item, index: item.index - 1 };
                    }
                    return item;
                }
            );
        newFsState.splice(index, 1);
        props.setValue(
            'discountBrokeragePricing.transactionFeeSchedule',
            newDiscountState,
            undefined,
            undefined,
            index
        );
        props.setValue(
            'fullServiceBrokeragePricing.transactionFeeSchedule',
            newFsState,
            undefined,
            undefined,
            index
        );
    };

    const addRow = (index: number) => {
        let newDiscountState =
            props.pricing.discountBrokeragePricing.transactionFeeSchedule.map(
                (item, idx) => {
                    if (idx > index) {
                        return { ...item, index: item.index + 1 };
                    }
                    return item;
                }
            );
        newDiscountState.splice(index + 1, 0, {
            feePercent: 0,
            revenueMax: 0,
            index: index + 1,
        });
        props.setValue(
            'discountBrokeragePricing.transactionFeeSchedule',
            newDiscountState,
            undefined,
            index + 1
        );

        let newFsState =
            props.pricing.fullServiceBrokeragePricing.transactionFeeSchedule.map(
                (item, idx) => {
                    if (idx > index) {
                        return { ...item, index: item.index + 1 };
                    }
                    return item;
                }
            );
        newFsState.splice(index + 1, 0, {
            feePercent: 0,
            revenueMax: 0,
            index: index + 1,
        });
        props.setValue(
            'fullServiceBrokeragePricing.transactionFeeSchedule',
            newFsState,
            undefined,
            index + 1
        );
    };

    const renderFeeRow = (fee: TransactionFeeScheduleItem, index: number) => {
        const discountItemKey = `discountBrokeragePricing.transactionFeeSchedule[${index}]`;
        const fullServiceItemKey = `fullServiceBrokeragePricing.transactionFeeSchedule[${index}]`;
        const previousItem =
            index > 0
                ? props.pricing.discountBrokeragePricing.transactionFeeSchedule[
                      index - 1
                  ]
                : undefined;
        return (
            <Row className="center-items">
                <Col md={3}>
                    <FormGroup>
                        <CustomNumberField
                            formKey={`${discountItemKey}.revenueMin`}
                            value={Helpers.formatToDollarValueWithCentsIfExist(
                                previousItem
                                    ? Number(previousItem.revenueMax)
                                    : 0
                            )}
                            updateValue={() => {}}
                            suffix={''}
                            disabled={true}
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        {props.pricing.discountBrokeragePricing
                            .transactionFeeSchedule.length !==
                        index + 1 ? (
                            <MarketplaceCurrencyField
                                name={`${discountItemKey}.revenueMax`}
                                value={fee.revenueMax}
                                setFormValue={(
                                    name: string,
                                    value: string
                                ): void => {
                                    props
                                        .setValue(
                                            `${discountItemKey}.revenueMax`,
                                            value
                                        )
                                        .then(() =>
                                            props.setValue(
                                                `${fullServiceItemKey}.revenueMax`,
                                                value
                                            )
                                        );
                                }}
                            />
                        ) : (
                            <CustomNumberField
                                formKey={`${discountItemKey}.revenueMax`}
                                value={
                                    '> ' +
                                    Helpers.formatToDollarValueWithCentsIfExist(
                                        props.pricing.discountBrokeragePricing
                                            .transactionFeeSchedule[index - 1]
                                            .revenueMax
                                    )
                                }
                                updateValue={() => {}}
                                suffix={''}
                                disabled={true}
                            />
                        )}
                    </FormGroup>
                </Col>
                <Col md={2}>
                    <FormGroup>
                        <MarketplacePercentField
                            name={`${discountItemKey}.feePercent`}
                            value={
                                props.pricing.discountBrokeragePricing
                                    .transactionFeeSchedule[index].feePercent
                            }
                            setValue={props.setValue}
                        />
                    </FormGroup>
                </Col>
                <Col md={2}>
                    <FormGroup>
                        <MarketplacePercentField
                            name={`${fullServiceItemKey}.feePercent`}
                            value={
                                props.pricing.fullServiceBrokeragePricing
                                    .transactionFeeSchedule[index].feePercent
                            }
                            setValue={props.setValue}
                        />
                    </FormGroup>
                </Col>
                <Col md={1}>
                    {props.pricing.discountBrokeragePricing
                        .transactionFeeSchedule.length > 2 ? (
                        <FontAwesomeIcon
                            icon={faXmark}
                            className="delete-btn"
                            onClick={() => {
                                deleteRow(index);
                            }}></FontAwesomeIcon>
                    ) : (
                        []
                    )}
                    <FontAwesomeIcon
                        icon={faPlus}
                        className="delete-btn"
                        onClick={() => {
                            addRow(index);
                        }}></FontAwesomeIcon>
                </Col>
            </Row>
        );
    };

    return (
        <div className="transaction-fee-schedule-table">
            <FormGroup className="fees-table">
                <Row className="center-items">
                    <Col md={3}>
                        <label>Revenue, Min</label>
                    </Col>
                    <Col md={3}>
                        <label>Revenue, Max</label>
                    </Col>
                    <Col md={2}>
                        <label>Discount Brokerage</label>
                    </Col>
                    <Col md={2}>
                        <label>Full-Service Brokerage</label>
                    </Col>
                    <Col md={1}></Col>
                </Row>
                {props.pricing.discountBrokeragePricing.transactionFeeSchedule.map(
                    (f, index) => renderFeeRow(f, index)
                )}
            </FormGroup>
        </div>
    );
};

export default TransactionFeeScheduleTable;
